import type { Category, DeviceAssessment } from '../api/client'
import { phrases } from './phrases'
import type { SaleState } from './sale'

/**
 * The guided flow: what to ask next, and which answers may be given.
 *
 * This is not a fallback bolted on for outages - it is the spine of the sale, and it runs whether or not
 * the assistant is available. The assistant changes how the conversation READS; it never changes what may
 * be answered. Every option below comes from a Cashit endpoint, so a customer can complete an entire sale
 * with OpenAI switched off and reach exactly the same lead at exactly the same price.
 */

export interface Choice {
  /** Stable within one question. */
  key: string
  label: string
  /** The smaller second line the design puts inside a choice chip. */
  hint?: string
  selected?: boolean
}

export interface Question {
  /** What the assistant says when it asks this. */
  prompt: string
  choices: Choice[]
  /** True when several answers may be given and the customer confirms with a "done" action. */
  multi: boolean
  /**
   * The confirm button of a multi-answer question, when at least one option is ticked.
   *
   * It is a PAIR because the button has to say two different things, and this file cannot decide between
   * them: the ticks live in the component's pending selection, not in the stored answers. A label computed
   * from the answers read "I have none of these" while three accessories were visibly ticked - the button
   * telling the customer the opposite of what they had just done.
   */
  confirmLabel?: string
  /** The same button when nothing is ticked, where "none" is itself a valid answer. */
  confirmEmptyLabel?: string
  /**
   * Several independent single-choice lists asked together - the RAM, processor and drive of a laptop.
   * They are one QUESTION ("is this really your specification?") rather than three, and asking them as
   * three separate steps would make an optional correction feel like an interrogation.
   */
  groups?: { key: string; label: string; choices: Choice[] }[]
  /** True when the customer may simply carry on without answering. */
  optional?: boolean
  /** True when the answer is typed into the composer rather than chosen. */
  freeText?: boolean
  placeholder?: string
}

/**
 * The customer-facing name of a catalogue option.
 *
 * ProblemName / AccsName is the sentence written FOR a customer ("Cannot make or receive calls"). LabelName
 * is a short internal tag for admin screens, and its content is not guaranteed to be presentable: six of the
 * mobile fault rows carry the label "Eligibility gate (M63)", which is a migration note. Preferring the tag
 * put that on screen as a thing a customer was asked to tick. So the full name wins, and the tag is only a
 * fallback for a row that somehow has no full name.
 */
const label = (full: string | undefined, tag: string | undefined) =>
  full?.trim() || tag?.trim() || ''

/**
 * The question for the current step, or null when the step is not a question (fetching a quote, signing
 * in, done). `assessment` is null until a model has been chosen, which is exactly when no question below
 * needs it.
 */
export function questionFor(
  state: SaleState,
  categories: Category[],
  assessment: DeviceAssessment | null,
  /** The language the conversation is in, so the guided questions are asked in it too. */
  language?: string,
): Question | null {
  const t = phrases(language)

  switch (state.step) {
    case 'category':
      return {
        prompt: t.category,
        multi: false,
        choices: categories.map((c) => ({
          key: `category:${c.id}`,
          label: c.categoryName,
        })),
      }

    case 'model':
      // A misspelt search: the closest catalogue names, offered as a question.
      if (state.candidates.length === 0 && state.suggestions.length > 0) {
        return {
          prompt: state.suggestions.length === 1 ? t.didYouMeanOne : t.didYouMeanMany,
          multi: false,
          choices: state.suggestions.map((name, index) => ({
            key: `suggestion:${index}`,
            label: name,
          })),
        }
      }

      // After a search, the matches are the choices. Before one, the customer types.
      return state.candidates.length > 0
        ? {
            prompt: t.whichOfThese,
            multi: false,
            choices: state.candidates.map((d) => ({
              key: `device:${d.deviceConfigId}`,
              label: d.name,
              hint: d.brand ?? undefined,
            })),
          }
        : {
            prompt: t.exactModel,
            multi: false,
            freeText: true,
            placeholder: t.exactModelPlaceholder,
            choices: [],
          }

    case 'configuration': {
      // A model group's configurations come FIRST: for phones and tablets each one is its own catalogue
      // device, so this is the only place the customer says whether they have the 128 GB or the 512 GB.
      // Skipping it priced them at whichever device the search happened to return.
      if (state.configurations.length > 1) {
        return {
          prompt: t.whichOneDoYouHave,
          multi: false,
          choices: state.configurations.map((c) => ({
            key: `groupconfig:${c.deviceId}`,
            // The catalogue name already carries the capacities ("Apple iPhone 15 6GB 256GB"), which is
            // exactly what the customer is being asked to recognise.
            label: c.name,
            hint: c.sellPrice > 0 ? `up to ₹${new Intl.NumberFormat('en-IN').format(c.sellPrice)}` : undefined,
            selected: state.device?.deviceConfigId === c.deviceId,
          })),
        }
      }

      const variants = assessment?.modelVariants ?? []
      // A model with one configuration - or none - has nothing to ask about. The caller skips the step.
      if (variants.length < 2) return null
      return {
        prompt: t.whichConfiguration,
        multi: false,
        choices: variants.map((v) => ({
          key: `configuration:${v.deviceVariantId}`,
          label: v.name,
          selected: state.answers.deviceVariantId === v.deviceVariantId,
        })),
      }
    }

    case 'components':
      // A FORM rather than a question of chips - the full platform list runs to 165 processors, and the
      // customer's own specification is what they are confirming, not picking from scratch. The step is
      // rendered by SpecPicker; this only keeps it in the flow and gives the conversation something to say.
      // Null when the model's category has no configurable components, so the step skips itself.
      return assessment?.hasModelVariants
        ? {
            prompt: t.confirmSpecification,
            multi: false,
            optional: true,
            choices: [],
          }
        : null

    case 'age': {
      const options = assessment?.durationTimes ?? []
      if (options.length === 0) return null
      return {
        prompt: t.howOld,
        multi: false,
        choices: options.map((d) => ({
          key: `age:${d.id}`,
          label: d.durationName,
          selected: state.answers.durationTimeId === d.id,
        })),
      }
    }

    case 'condition': {
      const options = assessment?.conditions ?? []
      if (options.length === 0) return null
      return {
        prompt: t.howDoesItLook,
        multi: true,
        confirmLabel: t.thatIsItsCondition,
        confirmEmptyLabel: t.noMarksAtAll,
        choices: options.map((c) => ({
          key: `condition:${c.id}`,
          label: c.conditionName,
          selected: state.answers.conditionIds.includes(c.id),
        })),
      }
    }

    case 'faults': {
      const options = assessment?.functionalProblems ?? []
      if (options.length === 0) return null
      return {
        prompt: t.anythingNotWorking,
        multi: true,
        confirmLabel: t.thatIsAllThatIsWrong,
        confirmEmptyLabel: t.everythingWorks,
        choices: options.map((f) => ({
          key: `fault:${f.id}`,
          label: label(f.problemName, f.labelName),
          selected: state.answers.faultIds.includes(f.id),
        })),
      }
    }

    case 'accessories': {
      const options = assessment?.accessories ?? []
      if (options.length === 0) return null
      return {
        prompt: t.whichDoYouStillHave,
        multi: true,
        confirmLabel: t.thatIsWhatIHave,
        confirmEmptyLabel: t.iHaveNoneOfThese,
        choices: options.map((a) => ({
          key: `accessory:${a.accessoryId}`,
          label: label(a.name, a.labelName),
          selected: state.answers.accessoryIds.includes(a.accessoryId),
        })),
      }
    }

    case 'pincode':
      return {
        prompt: t.pincode,
        multi: false,
        freeText: true,
        placeholder: t.pincodePlaceholder,
        choices: [],
      }

    default:
      return null
  }
}

/**
 * The step after this one, skipping any question the catalogue has no options for. Without this, a device
 * with no accessories mapped would stall on an empty question with nothing to click - the customer's sale
 * would simply stop, with no error to explain it.
 */
export function nextAnswerableStep(state: SaleState, assessment: DeviceAssessment | null): SaleState['step'] {
  const order: SaleState['step'][] = ['configuration', 'components', 'age', 'condition', 'faults', 'accessories', 'pincode']
  const from = order.indexOf(state.step)
  if (from === -1) return state.step

  for (let i = from + 1; i < order.length; i++) {
    const step = order[i]
    if (step === 'pincode') return step
    if (questionFor({ ...state, step }, [], assessment)) return step
  }
  return 'pincode'
}

/** The first step that can actually be asked once a model is known. */
export function firstAnswerableStep(state: SaleState, assessment: DeviceAssessment | null): SaleState['step'] {
  if (questionFor({ ...state, step: 'configuration' }, [], assessment)) return 'configuration'
  return nextAnswerableStep({ ...state, step: 'configuration' }, assessment)
}
